import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChatState } from '../Context/ChatProvider';
import { useToast } from '../ui';
import notificationSound from '../assets/bell.wav';
import eventAlertSound from '../assets/eventAlert.wav';
import sirenSound from '../assets/til_kazam.wav';
import { getLang, t } from '../i18n';
import { loadPrefs } from '../Chat/chatUtils';

const WorkspaceCtx = createContext(null);
export const useWorkspace = () => useContext(WorkspaceCtx);

/**
 * What the bell shows for a notification, in the language selected NOW.
 * Attendance reminders carry their text in every language (data.i18n); tasks keep their summary and the sentence is built here.
 * Notifications saved before this existed keep the text they were saved with.
 */
export function notifText(n) {
  if (n.kind === 'access') {
    const tx = n.i18n && (n.i18n[getLang()] || n.i18n.en);
    return { name: t("Attendance"), headline: tx ? tx.title : n.content, body: String((tx ? tx.body : n.body) || '').split('\n')[0] };
  }
  if (n.kind === 'task' && n.summary !== undefined) return { name: n.sender && n.sender.fullname, headline: t("Task - {{summary}}", { summary: n.summary }), body: t("Task - {{summary}}", { summary: n.summary }) };
  return { name: n.sender && n.sender.fullname, headline: n.content, body: n.content };
}

export const readStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem('userInfo')) || null;
  } catch (e) {
    return null;
  }
};

// Chrome and Safari refuse to play a sound until the person has clicked or typed somewhere on the page since it was loaded.
// When that happens we say so once, instead of staying silent.
let onSoundBlocked = null;
const playSound = (src, volume = 0.5) => {
  try {
    const a = new Audio(src);
    a.volume = volume;
    a.play().catch((e) => { if (e && e.name === 'NotAllowedError' && onSoundBlocked) onSoundBlocked(); });
  } catch (e) {
    /* no audio support */
  }
};

/**
 * Single owner of everything that used to be duplicated in every screen's navbar:
 * socket registration, presence, notifications, event alerts, incoming calls, user directory.
 */
export function WorkspaceProvider({ children }) {
  const { socket, address, setIsActive, isActive } = ChatState();
  const me = useMemo(readStoredUser, []);
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;
  const openChatRef = useRef(null); // the conversation that is on screen right now (set by the Chat screen)
  const warnedSound = useRef(false);

  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [notifs, setNotifs] = useState(() => (me && me.notifications) || []);
  const [incomingCall, setIncomingCall] = useState(null);
  const [eventAlert, setEventAlert] = useState(null);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [perm, setPerm] = useState(null); // { isAdmin, roleName, permissions: { feature: [actions] }, levels } of this user; null until loaded, false if it could not be loaded
  const levels = perm ? perm.levels : perm; // the older view: { screen: none | view | edit }

  const persistNotifs = useCallback(
    (arr) => {
      const u = readStoredUser();
      if (!u) return;
      u.notifications = arr;
      u.notLength = arr.filter((n) => !n.read).length;
      localStorage.setItem('userInfo', JSON.stringify(u));
      axios.post(`${address}/api/user/edit_user`, { items: u }).catch(() => {});
    },
    [address]
  );

  const pushNotif = useCallback(
    (n) => {
      setNotifs((prev) => {
        const next = [{ ...n, read: false }, ...prev].slice(0, 100);
        persistNotifs(next);
        return next;
      });
      playSound(notificationSound);
    },
    [persistNotifs]
  );

  const markRead = useCallback(
    (idx) => {
      setNotifs((prev) => {
        const next = prev.map((n, i) => (i === idx ? { ...n, read: true } : n));
        persistNotifs(next);
        return next;
      });
    },
    [persistNotifs]
  );

  /** the person opened this conversation: its messages are no longer news (the bell and the badge of the menu) */
  const markChatRead = useCallback((chatId) => {
    setNotifs((prev) => {
      if (!prev.some((n) => !n.read && n._id && n.chat && String(n.chat._id || n.chat) === String(chatId))) return prev;
      const next = prev.map((n) => (!n.read && n._id && n.chat && String(n.chat._id || n.chat) === String(chatId) ? { ...n, read: true } : n));
      persistNotifs(next);
      return next;
    });
  }, [persistNotifs]);

  const markAllRead = useCallback(() => {
    setNotifs((prev) => {
      const next = prev.map((n) => ({ ...n, read: true }));
      persistNotifs(next);
      return next;
    });
  }, [persistNotifs]);

  const clearNotifs = useCallback(() => {
    setNotifs([]);
    persistNotifs([]);
  }, [persistNotifs]);

  const fetchUsers = useCallback(async () => {
    try {
      const { data } = await axios.get(`${address}/api/user/users-info`);
      setUsers(Array.isArray(data) ? data : []);
    } catch (e) {
      /* offline: keep previous list */
    }
  }, [address]);

  const refreshProjects = useCallback(async () => {
    if (!me) return [];
    try {
      const { data } = await axios.get(`${address}/api/projects`, { params: { user: me._id } });
      setProjects(Array.isArray(data) ? data : []);
      return data;
    } catch (e) {
      return [];
    }
  }, [address, me]);

  useEffect(() => {
    fetchUsers();
    refreshProjects();
  }, [fetchUsers, refreshProjects]);

  // Socket wiring — registered once for the whole session.
  useEffect(() => {
    if (!me) return undefined;
    socket.emit('login', me);
    socket.emit('setup', me);

    const onOnline = (ids) => setIsActive(ids);
    // fingerprint terminal: reminders from the attendance module, and confirmation of my own punch
    const onAccessNotice = (n) => {
      pushNotif({ kind: 'access', noticeId: n._id, content: n.title, body: n.body, i18n: n.data && n.data.i18n, sender: { fullname: t("Attendance") }, updatedAt: n.createdAt });
      toast(notifText({ kind: 'access', content: n.title, i18n: n.data && n.data.i18n }).headline, 'warning');
    };
    const onAccessPunch = (p) => {
      if (String(p.userId) === String(me._id)) toast(t("✓ Attendance registered at {{time}}", { time: new Date(p.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) }), 'success');
    };
    const onTask = (task) =>
      pushNotif({
        kind: 'task',
        summary: task.summary,
        content: t("Task - {{summary}}", { summary: task.summary }),
        taskNum: task.taskNum,
        status: task.statux,
        sender: { fullname: task.author, img_user: task.img_user },
        updatedAt: new Date(),
      });
    const onMessage = (msg) => {
      const chatId = msg.chat && msg.chat._id;
      if (chatId && loadPrefs().muted.includes(chatId)) return; // a muted conversation stays quiet
      // no notification only when the person is looking at that very conversation
      const looking = pathRef.current === '/chat' && chatId && openChatRef.current === chatId && document.visibilityState === 'visible' && document.hasFocus();
      if (looking) return;
      pushNotif(msg);
    };
    const onCall = ({ roomId, from, title }) => setIncomingCall({ roomId, from, title, name: from && from.fullname });
    const onDeclined = ({ from }) => toast(t("{{name}} declined the call", { name: (from && from.fullname) || t("Someone") }), 'warning');
    const onEvent = (ev) => {
      playSound(eventAlertSound);
      setEventAlert(ev);
      setTimeout(() => setEventAlert(null), 10000);
    };
    const onIsrael = (alert) => {
      playSound(sirenSound);
      const list = Array.isArray(alert.cities) ? alert.cities : String(alert.cities || '').split(',');
      const cities = list.slice(0, 8).join(', ') + (list.length > 8 ? ` +${list.length - 8}` : '');
      toast(t("🚨 Alert in Israel — {{cities}}.", { cities }) + ` ${alert.type && alert.type !== 'none' ? `${alert.type}. ` : ''}${alert.instructions || ''}`, 'error');
    };

    const onPhoto = ({ _id, img_user }) => {
      setUsers((list) => list.map((u) => (u._id === _id ? { ...u, img_user } : u)));
      try { const mine = JSON.parse(localStorage.getItem('userInfo')); if (mine && mine._id === _id) localStorage.setItem('userInfo', JSON.stringify({ ...mine, img_user })); } catch (e) { /* ignore */ }
    };
    socket.on('user:photo', onPhoto);
    socket.on('online_chat', onOnline);
    socket.on('access:notice', onAccessNotice);
    socket.on('access:punch', onAccessPunch);
    socket.on('user_active_task', onTask);
    socket.on('message recieved', onMessage);
    socket.on('meet:incoming', onCall);
    socket.on('meet:declined', onDeclined);
    socket.on('event-alert', onEvent);
    socket.on('new-alert-il', onIsrael);
    return () => {
      socket.off('user:photo', onPhoto);
      socket.off('online_chat', onOnline);
      socket.off('access:notice', onAccessNotice);
      socket.off('access:punch', onAccessPunch);
      socket.off('user_active_task', onTask);
      socket.off('message recieved', onMessage);
      socket.off('meet:incoming', onCall);
      socket.off('meet:declined', onDeclined);
      socket.off('event-alert', onEvent);
      socket.off('new-alert-il', onIsrael);
    };
  }, [socket, me, pushNotif, setIsActive, toast, navigate]);

  // reminders received while offline show up in the bell on the next visit
  useEffect(() => {
    if (!me) return;
    axios.get(`${address}/api/access/notices`, { params: { unread: 1 } }).then(({ data }) => {
      [...data].reverse().forEach((n) => {
        if (!notifs.some((x) => x.noticeId === n._id)) pushNotif({ kind: 'access', noticeId: n._id, content: n.title, body: n.body, i18n: n.data && n.data.i18n, sender: { fullname: t("Attendance") }, updatedAt: n.createdAt });
      });
    }).catch(() => {});
  }, [address, me && me._id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadLevels = useCallback(() => axios.get(`${address}/api/permissions/me`).then((r) => setPerm(r.data)).catch(() => setPerm((l) => l || false)), [address]); // false: could not load (the API still enforces)
  useEffect(() => { if (me) loadLevels(); }, [me && me._id, loadLevels]); // eslint-disable-line react-hooks/exhaustive-deps
  /** may this user open (view) or change (edit) a screen? Everything is allowed while the permissions load; the API decides anyway. */
  // can('projects', 'edit') asks about a whole screen (none / view / edit); can('projects.cases', 'create') about one function and one action.
  const can = useCallback((key, need = 'view') => {
    if (!perm) return true;
    if (key === 'permissions') return !!perm.isAdmin; // managing roles is reserved for system administrators
    if (String(key).includes('.')) return perm.isAdmin || (perm.permissions[key] || []).includes(need);
    const rank = { none: 0, view: 1, edit: 2 };
    return rank[(perm.levels || {})[key] || 'none'] >= rank[need];
  }, [perm]);
  /** true when the person may do `need` on at least one of these functions */
  const canAny = useCallback((keys, need = 'view') => keys.some((k) => can(k, need)), [can]);

  // Ctrl/Cmd+K opens the command palette from anywhere.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    onSoundBlocked = () => {
      if (warnedSound.current) return;
      warnedSound.current = true;
      toast(t("The browser blocks sounds until you click on the page. Click anywhere once to hear notifications."), 'warning');
    };
    return () => { onSoundBlocked = null; };
  }, [toast]);

  const onlineIds = useMemo(() => new Set(isActive || []), [isActive]);
  const unread = notifs.filter((n) => !n.read).length;

  // unread count in the browser tab, so a message is noticed from another tab or window too
  useEffect(() => {
    const base = document.title.replace(/^\(\d+\)\s*/, '');
    document.title = unread > 0 ? `(${unread}) ${base}` : base;
    return () => { document.title = base; };
  }, [unread]);

  const value = {
    me,
    users,
    fetchUsers,
    projects,
    refreshProjects,
    onlineIds,
    notifs,
    unread,
    pushNotif,
    openChatRef,
    markRead,
    markAllRead,
    markChatRead,
    clearNotifs,
    incomingCall,
    setIncomingCall,
    eventAlert,
    setEventAlert,
    paletteOpen,
    setPaletteOpen,
    levels,
    can,
    canAny,
    roleName: perm ? perm.roleName : null,
    reloadPermissions: loadLevels,
  };
  return <WorkspaceCtx.Provider value={value}>{children}</WorkspaceCtx.Provider>;
}
